/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { omit } from 'lodash-es';

import {
  selectArnmAccidentsState,
  filtersModalRulesArnmAccidentsChange,
  resetModalArnmAccidentsChange,
  filtersModalConditionArnmAccidentsChange,
} from '@store';
import { getCleanedUrlParams, showError } from '@utils';
import { BOARDS_ID } from '@constants';
import { QUERY_KEYS } from 'constants/queryKeys';
import { boardsServices } from '@services';
import { useQuery } from 'react-query';
import { useAssignees, useServicePoints } from 'hooks/common';
import {
  getArnmAccidentsColumns,
  getArnmAccidentsPayload,
  handleArnmAccidentsData,
  normalizeOrderBy,
} from './utils';

export const ARNM_ACCIDENTS_EXPORT_LIMIT = 100000;
export const ARM_ACCIDENTS_PAGE_SIZE = 500;

export const useArnmAccidentsOperations = () => {
  const router = useRouter();
  const { servicePoints, isFetching: isServicePointsFetching } =
    useServicePoints();
  const { filters } = useSelector(selectArnmAccidentsState);
  const dispatch = useDispatch();
  const {
    assignees,
    assigneesWithNone,
    isFetching: isAssigneesFetching,
    isFetched: isAssigneesFetched,
  } = useAssignees();
  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || ARM_ACCIDENTS_PAGE_SIZE;
  const orderBy = router.query?.orderBy || 'createdAt';
  const order = router.query?.order || 'desc';
  const query = router.query?.query;

  const cleanedParams = getCleanedUrlParams({
    ...omit(router.query, ['view']),
    page,
    pageSize: parseInt(pageSize, 10) || ARM_ACCIDENTS_PAGE_SIZE,
    orderBy: normalizeOrderBy(orderBy),
    order,
    query,
  });

  const arnmAccidentsResponse = useQuery(
    [
      QUERY_KEYS.GET_ARNM_ACCIDENTS_KEY,
      { cleanedParams, filters: filters.modalFiltersRules },
    ],
    () => {
      const urlParams = getCleanedUrlParams({
        page,
        orderBy: normalizeOrderBy(orderBy),
        order,
        pageSize: parseInt(pageSize, 10) || ARM_ACCIDENTS_PAGE_SIZE,
        query,
      });
      const payload = getArnmAccidentsPayload(
        cleanedParams,
        filters.modalFiltersRules
      );
      return boardsServices.getBoardTasks({
        boardId: BOARDS_ID.ARM_ACCIDENTS,
        params: urlParams,
        payload,
      });
    },
    { enabled: router.isReady && isAssigneesFetched }
  );

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, BOARDS_ID.ARM_ACCIDENTS],
    () => boardsServices.getEnumsPerBoardId(BOARDS_ID.ARM_ACCIDENTS),
    { enabled: router.isReady }
  );

  const handleChangeRules = (rules) => {
    dispatch(filtersModalRulesArnmAccidentsChange(rules));
  };

  const resetModalChange = useCallback(() => {
    dispatch(resetModalArnmAccidentsChange());
  }, [dispatch]);

  const handleFiltersModalChange = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionArnmAccidentsChange(conditions));
    },
    [dispatch]
  );

  const getAccidentsExportPayload = () => {
    return {
      boardId: BOARDS_ID.ARM_ACCIDENTS,
      payload: {
        type: 'csv',
        rule: getArnmAccidentsPayload(cleanedParams, filters.modalFiltersRules),
      },
      params: getCleanedUrlParams({
        orderBy: normalizeOrderBy(orderBy),
        order,
        query,
      }),
    };
  };

  useEffect(() => {
    if (arnmAccidentsResponse?.error) {
      showError(arnmAccidentsResponse?.error);
    }
  }, [arnmAccidentsResponse?.error]);

  const arnmAccidents = handleArnmAccidentsData(
    arnmAccidentsResponse?.data?.data || [],
    assignees
  );
  const columns = getArnmAccidentsColumns(arnmAccidents);

  return {
    page,
    total: arnmAccidentsResponse?.data?.meta?.total,
    pageSize: parseInt(pageSize, 10) || ARM_ACCIDENTS_PAGE_SIZE,
    loading:
      arnmAccidentsResponse?.isFetching ||
      isAssigneesFetching ||
      isServicePointsFetching,
    arnmAccidents,
    columns,
    assignees,
    assigneesWithNone,
    servicePoints,
    filters,
    enums: enumsResponse?.data?.data || {},
    getArnmAccidents: arnmAccidentsResponse.refetch,
    getAccidentsExportPayload,
    handleFiltersModalChange,
    handleChangeRules,
    resetModalChange,
  };
};
